import { Switch } from "@mui/material";
import {
	type GridColDef,
	type GridRenderCellParams,
} from "@mui/x-data-grid-pro";

import { GroupUser } from "@/components/global/types";

import { customNameComparator } from "../../../../../helpers/coappDataGridUtils";
import { getUserColumnFilterValue } from "../../../../../helpers/datagridFiltering";
import CoAppDataGridDateTimeCell from "../../../../global/components/datagrid/CoAppDataGridDateTimeCell";
import CoAppDataGridListCell from "../../../../global/components/datagrid/CoAppDataGridListCell";
import CoAppDataGridNameCell from "../../../../global/components/datagrid/CoAppDataGridNameCell";
import ThreeDotMenu from "../../../../global/components/ThreeDotMenu";

export const groupUserColumns = [
	"actions",
	"user",
	"roles",
	"groups",
	"status",
	"lastExtensionHeartbeat",
	"createdAt",
];

export interface GroupUsersColumnsProps {
  organizationSsoStatus: boolean;
  handleUserStatusToggle: (
    userId: GroupUser["id"],
    state: "false" | "true"
  ) => void;
  viewUserPage: (
    params: GridRenderCellParams<GroupUser>,
    navIndex: number,
    groupId: string,
    stateName: string
  ) => void;
  navigationItemReferences: { groupNavItemIndex: number } & Record<
    string,
    number
  >;
  groupId: string;
  stateName: string;
  removeUserFromGroup: (userId: GroupUser["id"]) => void;
  currentUserIsCoAppSuperAdmin: boolean;
}

/**
 * Returns the column definitions for the Group Users DataGrid.
 */
export default function GroupUsersColumns({
	organizationSsoStatus,
	handleUserStatusToggle,
	viewUserPage,
	navigationItemReferences,
	groupId,
	stateName,
	removeUserFromGroup,
	currentUserIsCoAppSuperAdmin,
}: GroupUsersColumnsProps): GridColDef<GroupUser>[] {
	return [
		{
			editable: false,
			field: "user",
			flex: 1,
			headerName: "User",
			sortable: true,
			sortComparator: (v1: any, v2: any) => customNameComparator(v1, v2) as number,
			valueGetter: (value, row) => getUserColumnFilterValue(value, row),
			pinnable: false,
			renderCell: (params: GridRenderCellParams<GroupUser>) => (
				<CoAppDataGridNameCell item={params.row} isUser />
			),
		},
		{
			editable: false,
			field: "roles",
			flex: 1,
			headerName: "Roles",
			sortable: false,
			pinnable: false,
			renderCell: (params: GridRenderCellParams<GroupUser, string[]>) => (
				<CoAppDataGridListCell items={params.value} resource="Roles" />
			),
		},
		{
			disableColumnMenu: true,
			editable: false,
			field: "groups",
			filterable: false,
			sortable: false,
			flex: 1,
			headerName: "Groups",
			pinnable: false,
			renderCell: (params: GridRenderCellParams<GroupUser, string[]>) => (
				<CoAppDataGridListCell items={params.value} resource="Groups" />
			),
		},
		{
			editable: false,
			field: "active",
			width: 90,
			headerName: "Status",
			sortable: true,
			renderCell: (params: GridRenderCellParams<GroupUser>) => (
				<Switch
							checked={params.row.active}
							disabled={organizationSsoStatus}
							onChange={() =>
								handleUserStatusToggle(
									params.row.id,
									params.row.active ? "false" : "true"
								)
							}
						/>
			),
		},
		{
			editable: false,
			field: "lastHeartbeat",
			width: 200,
			headerName: "Last Heartbeat",
			pinnable: false,
			sortable: true,
			valueGetter: (params: any) => (params?.value ? new Date(params.value) : null),
			renderCell: (params: GridRenderCellParams<GroupUser, Date | null>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
		{
			editable: false,
			field: "createdAt",
			width: 200,
			headerName: "Created",
			pinnable: false,
			sortable: true,
			valueGetter: (params: any) => (params.value ? new Date(params.value) : null),
			renderCell: (params: GridRenderCellParams<GroupUser, Date | null>) => (
				<CoAppDataGridDateTimeCell value={params.value} />
			),
		},
		{
			field: "actions",
			flex: 0.1,
			sortable: false,
			type: "actions",
			renderCell: (params: GridRenderCellParams<GroupUser>) => (
				<ThreeDotMenu
							options={
								organizationSsoStatus
									? [
										{
											name: "View User",
											optionClickHandler: () =>
												viewUserPage(
													params,
													navigationItemReferences.groupNavItemIndex,
													groupId,
													stateName
												),
										},
									]
									: [
										...(!params.row.isCoAppSuperAdmin ||
                      currentUserIsCoAppSuperAdmin
											? [
												{
													name: "Remove From Group",
													optionClickHandler: () =>
														removeUserFromGroup(params.row.id),
												},
											]
											: []),
										{
											name: "View User",
											optionClickHandler: () =>
												viewUserPage(
													params,
													navigationItemReferences.groupNavItemIndex,
													groupId,
													stateName
												),
										},
									]
							}
						/>
			),
		},
	];
}
